import { useLazyQuery, useMutation } from "@apollo/client";
import { notifications } from "@mantine/notifications";

import {
  CarrierType,
  isIntegratedCarrier,
  GET_PICKUP_BY_DATE,
  EDIT_PICKUP,
  Pickup,
  ShippingType,
} from "@jasper/shared";

export const usePickup = () => {
  const [getPickupByDate, { loading: isFetchPickupsLoading }] = useLazyQuery(
    GET_PICKUP_BY_DATE,
    { fetchPolicy: "network-only" }
  );

  const [editPickupMutation] = useMutation(EDIT_PICKUP);

  const editPickupById = async (
    pickupId: string,
    data: {
      clinicClosingTime: string;
      packageReadyTime: string;
      date: string;
      comment: string | null;
      pickupCarrier: CarrierType;
      trackingNumber?: string;
    },
    connectedUserEmail: string
  ): Promise<Pickup | null> => {
    try {
      if (
        isIntegratedCarrier(
          data.pickupCarrier,
          ShippingType.PICKUP,
          connectedUserEmail
        )
      ) {
        notifications.show({
          title: `Pickup with ${data.pickupCarrier} cannot be updated`,
          color: "red",
          message: "",
        });
        return null;
      }
      const req = await editPickupMutation({
        variables: {
          pickupId: pickupId,
          where: {
            id: pickupId,
          },
          trackingNumber: data?.trackingNumber ?? null,
          data: {
            closingTime: { set: data.clinicClosingTime },
            readyTime: { set: data.packageReadyTime },
            comment: { set: data.comment },
            scheduledDate: { set: data.date },
          },
        },
      });
      notifications.show({
        title: `Successfully updated pickup`,
        color: "green",
        message: "",
      });
      return req.data.editPickup;
    } catch (e) {
      console.error(e);
      notifications.show({
        title: `Error while trying to edit pickup ${pickupId}`,
        color: "red",
        message: "",
      });
      return null;
    }
  };

  const fetchPickups = async (
    selectedDate: Date,
    take: number = 50,
    skip: number = 0
  ) => {
    try {
      const req = await getPickupByDate({
        variables: {
          selectedDate: selectedDate,
          take: take,
          skip: skip,
        },
      });
      return {
        data: req?.data?.getPickupByDate?.data ?? [],
        totalCount: req?.data?.getPickupByDate?.totalCount ?? 0,
      };
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to fetch pickups",
        color: "red",
        message: "",
      });
      return {
        data: [],
        totalCount: 0,
      };
    }
  };

  return {
    fetchPickups,
    editPickupById,
    isLoading: isFetchPickupsLoading,
  };
};
